/**
 * Phase 8 T-26 §29.E / §29.F — Smart Match intake JD-mode tab row.
 *
 *   `[ Job description | Bulk resumes | Refine Search ]`
 *
 * 21-Apr follow-up — Single-JD and Multi-JD are now unified into one
 * "Job description" tab. The same tab handles 1..10 JDs: when the user
 * has exactly one JD they see the freeform composer; they can click
 * `[+ Add another JD]` to graduate to the multi-card editor without
 * switching tabs. Bulk resumes stays separate because the UX (file
 * dropzone, parse progress, score-against-JD context) is meaningfully
 * different.
 *
 * Selection drives which body renders inside the intake shell (T-22
 * primitives stay unchanged). The last user selection is persisted in
 * `localStorage.ggj.smartmatch.jdMode` so re-opening intake returns the
 * user to their preferred mode. Legacy `'single' | 'multi'` values
 * persisted by older builds are transparently migrated to `'job'` on
 * read.
 */

import { useCallback } from 'react';

/** The four intake tabs. 'preferences' opens the preferences modal inline. */
export type JdMode = 'job' | 'bulk' | 'refine' | 'preferences';

const STORAGE_KEY = 'ggj.smartmatch.jdMode';

const LABELS: Record<JdMode, string> = {
  job: 'Job description',
  preferences: 'Preferences',
  bulk: 'Bulk resumes',
  refine: 'Refine Search',
};

/** Desired order: Refine Search → Bulk resumes → Preferences → Job description */
const ORDER: readonly JdMode[] = ['refine', 'bulk', 'preferences', 'job'] as const;

/**
 * Read the persisted mode. Defaults to `'job'`. Legacy values
 * (`'single'` / `'multi'`) from pre-unification builds are migrated
 * transparently so the first re-open doesn't dump users onto a
 * tab they didn't pick.
 */
export function readJdMode(): JdMode {
  if (typeof window === 'undefined') return 'job';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'job' || raw === 'bulk' || raw === 'refine') return raw;
    if (raw === 'single' || raw === 'multi') return 'job';
    // 'preferences' is never persisted as a last-mode since it triggers a
    // modal, not a body swap. Fall through to 'job' in that case.
  } catch {
    /* localStorage may throw in private/quota modes — fall through. */
  }
  return 'job';
}

/** Persist the mode. Silent on quota/private-mode errors. */
export function writeJdMode(mode: JdMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* no-op */
  }
}

interface JdModeTabsProps {
  value: JdMode;
  onChange: (next: JdMode) => void;
  /**
   * Called when the user clicks the Preferences tab instead of switching
   * the intake body. The parent opens the modal; the tab never persists as
   * the active mode.
   */
  onPreferences?: () => void;
  /**
   * Optional per-tab disabled state (e.g. to hide Multi-JD behind a flag).
   * Unused today; kept so a feature-flag toggle is a one-line change.
   */
  disabled?: Partial<Record<JdMode, boolean>>;
}

export function JdModeTabs({ value, onChange, onPreferences, disabled = {} }: JdModeTabsProps) {
  const handle = useCallback(
    (next: JdMode) => {
      if (disabled[next]) return;
      if (next === 'preferences') {
        onPreferences?.();
        return;
      }
      onChange(next);
      writeJdMode(next);
    },
    [disabled, onChange, onPreferences],
  );

  return (
    <div
      role="tablist"
      aria-label="Intake mode"
      className="inline-flex rounded-[10px] border p-0.5 text-[12px] font-medium"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-elevated)',
      }}
    >
      {ORDER.map((m) => {
        const active = value === m;
        const isDisabled = !!disabled[m];
        return (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={isDisabled}
            onClick={() => handle(m)}
            className={`inline-flex h-7 items-center rounded-[8px] px-3 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              active
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                : 'text-[color:var(--vscode-text-secondary)] hover:text-[color:var(--vscode-text-primary)]'
            }`}
          >
            {LABELS[m]}
          </button>
        );
      })}
    </div>
  );
}
